import React, { useMemo, useState } from 'react';
import {
  Modal,
  BlockStack,
  TextField,
  Select,
  Text,
  InlineStack,
  Banner,
  Spinner,
} from '@shopify/polaris';
import { generationImportService } from '../../services/generationImportService';

const ImportGenerationTemplatesModal = ({ isOpen, onClose, onImported }) => {
  const [spreadsheet, setSpreadsheet] = useState('');
  const [storiesTab, setStoriesTab] = useState('Stories');
  const [scenesTab, setScenesTab] = useState('Scenes');
  const [driveFolder, setDriveFolder] = useState('');
  const [numberingMode, setNumberingMode] = useState('split_subfolders');
  const [imagesSubfolder, setImagesSubfolder] = useState('Images');
  const [masksSubfolder, setMasksSubfolder] = useState('Masks');
  const [digits, setDigits] = useState('2');
  const [extension, setExtension] = useState('png');

  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [job, setJob] = useState(null);
  const [error, setError] = useState('');

  const canPreview = useMemo(() => Boolean(spreadsheet.trim()), [spreadsheet]);
  const canExecute = useMemo(() => Boolean(preview?.data?.preview_id), [preview]);

  const resetState = () => {
    setSpreadsheet('');
    setStoriesTab('Stories');
    setScenesTab('Scenes');
    setDriveFolder('');
    setNumberingMode('split_subfolders');
    setImagesSubfolder('Images');
    setMasksSubfolder('Masks');
    setDigits('2');
    setExtension('png');
    setIsPreviewing(false);
    setIsExecuting(false);
    setPreview(null);
    setJob(null);
    setError('');
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const buildPayload = () => ({
    spreadsheet: {
      id: spreadsheet.trim(),
      stories_tab: storiesTab.trim() || 'Stories',
      scenes_tab: scenesTab.trim() || 'Scenes',
    },
    drive: {
      folder_id: driveFolder.trim(),
      numbering_mode: numberingMode,
      images_subfolder: imagesSubfolder.trim() || 'Images',
      masks_subfolder: masksSubfolder.trim() || 'Masks',
      digits: Number(digits) || 2,
      extension: extension.trim() || 'png',
    },
    options: {
      strict: true,
      upsert: true,
    }
  });

  const handlePreview = async () => {
    if (!canPreview) return;
    setIsPreviewing(true);
    setError('');
    setJob(null);
    try {
      const result = await generationImportService.previewGoogleImport(buildPayload());
      setPreview(result);
    } catch (err) {
      setPreview(null);
      setError(err.message || 'Preview failed');
    } finally {
      setIsPreviewing(false);
    }
  };

  const pollJobUntilDone = async (jobId) => {
    const maxTries = 180;
    for (let i = 0; i < maxTries; i++) {
      const jobResult = await generationImportService.getImportJob(jobId);
      setJob(jobResult?.data || null);
      const status = jobResult?.data?.status;
      if (status === 'completed' || status === 'failed') {
        return jobResult?.data;
      }
      await new Promise((resolve) => setTimeout(resolve, 1500));
    }
    throw new Error('Import polling timeout');
  };

  const handleExecute = async () => {
    if (!canExecute) return;
    setIsExecuting(true);
    setError('');
    try {
      const previewId = preview.data.preview_id;
      const run = await generationImportService.executeGoogleImport(previewId);
      const jobId = run?.data?.job_id;
      if (!jobId) {
        throw new Error('No job id returned from execute endpoint');
      }
      const finalJob = await pollJobUntilDone(jobId);
      if (finalJob.status === 'completed') {
        if (onImported) onImported(finalJob);
      } else {
        throw new Error(finalJob.error || 'Import job failed');
      }
    } catch (err) {
      setError(err.message || 'Execute failed');
    } finally {
      setIsExecuting(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={<div style={{ padding: '16px' }}>Bulk Import Generation Templates</div>}
      primaryAction={{
        content: isExecuting ? 'Importing...' : 'Import',
        onAction: handleExecute,
        loading: isExecuting,
        disabled: isPreviewing || isExecuting || !canExecute || Boolean(preview?.data?.errors?.length),
      }}
      secondaryActions={[
        {
          content: isPreviewing ? 'Validating...' : 'Validate',
          onAction: handlePreview,
          loading: isPreviewing,
          disabled: isPreviewing || isExecuting || !canPreview,
        },
        {
          content: 'Cancel',
          onAction: handleClose,
          disabled: isExecuting,
        },
      ]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          {error ? (
            <Banner tone="critical">
              <Text as="p">{error}</Text>
            </Banner>
          ) : null}

          <TextField
            label="Google Spreadsheet URL or ID"
            value={spreadsheet}
            onChange={setSpreadsheet}
            autoComplete="off"
            placeholder="https://docs.google.com/spreadsheets/d/..."
          />

          <InlineStack gap="300">
            <div style={{ minWidth: 220 }}>
              <TextField
                label="Stories Tab"
                value={storiesTab}
                onChange={setStoriesTab}
                autoComplete="off"
              />
            </div>
            <div style={{ minWidth: 220 }}>
              <TextField
                label="Scenes Tab"
                value={scenesTab}
                onChange={setScenesTab}
                autoComplete="off"
              />
            </div>
          </InlineStack>

          <TextField
            label="Google Drive Root Folder URL or ID (optional if scene_url/mask_url provided)"
            value={driveFolder}
            onChange={setDriveFolder}
            autoComplete="off"
            placeholder="https://drive.google.com/drive/folders/..."
          />

          <Select
            label="Drive Layout"
            options={[
              { label: 'Split subfolders (Images / Masks)', value: 'split_subfolders' },
              { label: 'Single folder paired by order', value: 'paired_by_order' },
            ]}
            value={numberingMode}
            onChange={setNumberingMode}
          />

          {numberingMode === 'split_subfolders' ? (
            <InlineStack gap="300">
              <div style={{ minWidth: 220 }}>
                <TextField
                  label="Images Subfolder"
                  value={imagesSubfolder}
                  onChange={setImagesSubfolder}
                  autoComplete="off"
                  placeholder="Images"
                />
              </div>
              <div style={{ minWidth: 220 }}>
                <TextField
                  label="Masks Subfolder"
                  value={masksSubfolder}
                  onChange={setMasksSubfolder}
                  autoComplete="off"
                  placeholder="Masks"
                />
              </div>
            </InlineStack>
          ) : null}

          <InlineStack gap="300">
            <div style={{ minWidth: 150 }}>
              <TextField
                label="Numbering Digits"
                type="number"
                value={digits}
                onChange={setDigits}
                autoComplete="off"
                min="1"
              />
            </div>
            <div style={{ minWidth: 220 }}>
              <TextField
                label="File Extension"
                value={extension}
                onChange={setExtension}
                autoComplete="off"
                placeholder="png"
              />
            </div>
          </InlineStack>

          {preview?.data?.summary ? (
            <div style={{ border: '1px solid #e3e3e3', borderRadius: 8, padding: 12 }}>
              <BlockStack gap="200">
                <Text as="p" variant="headingSm">Validation Summary</Text>
                <Text as="p">Stories: {preview.data.summary.stories_rows} rows</Text>
                <Text as="p">Scenes: {preview.data.summary.scenes_rows} rows</Text>
                <Text as="p">Create: {preview.data.summary.stories_create} stories, {preview.data.summary.scenes_create} scenes</Text>
                <Text as="p">Update: {preview.data.summary.stories_update} stories, {preview.data.summary.scenes_update} scenes</Text>
                <Text as="p" tone={preview.data.summary.errors > 0 ? 'critical' : 'success'}>
                  Errors: {preview.data.summary.errors}
                </Text>
                <Text as="p">Warnings: {preview.data.summary.warnings}</Text>
              </BlockStack>
            </div>
          ) : null}

          {(preview?.data?.errors || []).length > 0 ? (
            <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #f1c0c0', borderRadius: 8, padding: 8 }}>
              <BlockStack gap="100">
                {(preview.data.errors || []).slice(0, 30).map((e, idx) => (
                  <Text as="p" key={`${e.code}-${idx}`} tone="critical">
                    {e.tab ? `${e.tab} ` : ''}{e.row ? `row ${e.row}: ` : ''}{e.message}
                  </Text>
                ))}
              </BlockStack>
            </div>
          ) : null}

          {isExecuting && job ? (
            <InlineStack gap="200" align="center">
              <Spinner size="small" />
              <Text as="p">Running import... {job.progress?.scenes_processed || 0}/{job.progress?.scenes_total || 0} scenes</Text>
            </InlineStack>
          ) : null}

          {!isExecuting && job && job.status === 'completed' ? (
            <Banner tone="success">
              <Text as="p">Import completed. Stories created: {job.result?.stories_created || 0}, updated: {job.result?.stories_updated || 0}. Scenes created: {job.result?.scenes_created || 0}, updated: {job.result?.scenes_updated || 0}.</Text>
            </Banner>
          ) : null}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
};

export default ImportGenerationTemplatesModal;
